// Workflows listing — dense table plus a left-overlay configuration panel.
// Replaces the card grid in ChatbotsPage.tsx. See docs/specs/workflows-listing-split-view.md.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tab,
  TabList,
  Tabs,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdMenuBook, MdQrCode2, MdSearch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/client/components/shared/LoadingSpinner';
import { EmptyState } from '@/client/components/shared/EmptyState';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import {
  useChatbots,
  useChatbotStatus,
  useDeleteChatbot,
  useExportChatbotQrCodes,
  useReorderChatbots,
} from '@/lib/sdk/chatbot/queries';
import { Chatbot } from '@/lib/sdk/chatbot/types';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';
import { CreateMethodDialog } from '@/components/Chatbot/CreateMethodDialog';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { WorkflowTable } from '../components/workflow-list/WorkflowTable';
import { WorkflowBulkBar } from '../components/workflow-list/WorkflowBulkBar';
import { WorkflowConfigPanel } from '../components/workflow-list/WorkflowConfigPanel';
import { WorkflowQrModal } from '../components/workflow-list/WorkflowQrModal';
import { reminderSummary } from '../components/workflow-list/flowState';
import { useWorkflowSelection } from '../hooks/useWorkflowSelection';
import { useDepartments } from '../hooks/useDepartments';

type SortMode = 'custom' | 'name' | 'recent';
type TypeFilter = 'all' | 'static' | 'form';
// 'all' shows everything; 'none' isolates workflows with no department assigned.
type DepartmentFilter = string;

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'custom', label: 'Custom order' },
  { value: 'name', label: 'Name' },
  { value: 'recent', label: 'Recently created' },
];

const byTitle = (a: Chatbot, b: Chatbot) => a.title.localeCompare(b.title);
const byNewest = (a: Chatbot, b: Chatbot) => b.created_at.localeCompare(a.created_at);

/**
 * Applies the client's optimistic ordering while a reorder is in flight. Ids missing from
 * the pending list keep their server position, which is what happens to the tab that was
 * not being dragged.
 */
function applyPendingOrder(workflows: Chatbot[], pendingOrder: string[] | null): Chatbot[] {
  if (!pendingOrder) return workflows;
  const rank = new Map(pendingOrder.map((id, index) => [id, index]));
  return [...workflows].sort(
    (a, b) =>
      (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
}

export function WorkflowsPage() {
  const { user, currentOrg, featurePermissions, userPermission } = useAuth();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [initializedOrg, setInitializedOrg] = useState<string | null>(null);
  // null = the tab follows the open workflow; a number = the user picked a tab.
  const [tabOverride, setTabOverride] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [sortMode, setSortMode] = useState<SortMode>('custom');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [departmentFilter, setDepartmentFilter] = useState<DepartmentFilter>('all');
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  // Not URL-backed like the panel: a QR view is a momentary action, not a place.
  const [qrWorkflow, setQrWorkflow] = useState<Chatbot | null>(null);
  const createDialog = useDisclosure();
  const bulkDeleteDialog = useDisclosure();
  const bulkStatusDialog = useDisclosure();
  const [isBulkBusy, setBulkBusy] = useState(false);

  const { selectedCode, selectWorkflow, clearSelection } = useWorkflowSelection();

  const isSuperAdmin = user?.is_super_admin ?? false;
  const canAdd = helpers.hasFeatureAction(
    featurePermissions,
    userPermission,
    isSuperAdmin,
    'workflows',
    'add'
  );
  // The workflow guide needs the org's ai_chat feature on top of workflows access
  const hasGuide = isSuperAdmin || helpers.hasFeatureAccess(currentOrg?.feature_access, 'ai_chat');
  const navigate = useNavigate();

  const storageKey = `chatbots-search-${currentOrg!.id}`;
  const { getItem, setItem, removeItem } = useLocalStorage<string>(storageKey, {
    persistAcrossSessions: true,
  });

  const { data: chatbots, isLoading, error } = useChatbots({
    orgId: currentOrg!.id,
    limit: 100,
  });

  const { departments } = useDepartments(currentOrg!.id);

  const statusMutation = useChatbotStatus();
  const deleteMutation = useDeleteChatbot();
  const reorderMutation = useReorderChatbots();
  const qrExportMutation = useExportChatbotQrCodes();

  // Restore the persisted search once per org. Adjusting state during render is the
  // documented pattern for "reset when a prop changes" — an effect would render the
  // wrong search box for a frame.
  if (initializedOrg !== currentOrg!.id) {
    setInitializedOrg(currentOrg!.id);
    setSearchQuery(getItem() ?? '');
  }

  useEffect(() => {
    if (searchQuery) {
      setItem(searchQuery);
    } else {
      removeItem();
    }
  }, [searchQuery, setItem, removeItem]);

  const { activeWorkflows, inactiveWorkflows } = useMemo(() => {
    if (!chatbots?.items) return { activeWorkflows: [], inactiveWorkflows: [] };
    let workflows = chatbots.items;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      workflows = workflows.filter(
        (workflow) =>
          workflow.title.toLowerCase().includes(query) ||
          workflow.short_code.toLowerCase().includes(query)
      );
    }
    if (typeFilter !== 'all') {
      const botType = typeFilter === 'static' ? 'static' : 'dynamic';
      workflows = workflows.filter((workflow) => workflow.bot_type === botType);
    }
    if (departmentFilter !== 'all') {
      workflows = workflows.filter((workflow) =>
        departmentFilter === 'none'
          ? !workflow.department_key
          : workflow.department_key === departmentFilter
      );
    }
    // 'custom' keeps the server's order, which is sort_order NULLS LAST then created_at.
    const order = (list: Chatbot[]) => {
      if (sortMode === 'name') return [...list].sort(byTitle);
      if (sortMode === 'recent') return [...list].sort(byNewest);
      return applyPendingOrder(list, pendingOrder);
    };
    return {
      activeWorkflows: order(workflows.filter((workflow) => !workflow.blocked)),
      inactiveWorkflows: order(workflows.filter((workflow) => workflow.blocked)),
    };
  }, [chatbots, searchQuery, typeFilter, departmentFilter, sortMode, pendingOrder]);

  // With no explicit tab choice, the tab follows the open workflow — so a deep link to an
  // inactive workflow does not land on an Active tab that cannot show it.
  const openIsInactive = selectedCode
    ? inactiveWorkflows.some((workflow) => workflow.short_code === selectedCode)
    : false;
  const tabIndex = tabOverride ?? (openIsInactive ? 1 : 0);
  const rows = tabIndex === 0 ? activeWorkflows : inactiveWorkflows;

  // The URL is the source of truth for which workflow the panel is showing.
  const openWorkflow = useMemo(() => {
    if (!selectedCode) return null;
    return (
      [...activeWorkflows, ...inactiveWorkflows].find(
        (workflow) => workflow.short_code === selectedCode
      ) ?? null
    );
  }, [activeWorkflows, inactiveWorkflows, selectedCode]);

  const handleConfigure = useCallback(
    (chatbot: Chatbot) => {
      setTabOverride(null);
      selectWorkflow(chatbot.short_code);
    },
    [selectWorkflow]
  );

  const handleCheck = useCallback((id: string) => {
    setCheckedIds((current) =>
      current.includes(id) ? current.filter((checkedId) => checkedId !== id) : [...current, id]
    );
  }, []);

  const handleCheckAll = useCallback(
    (checked: boolean) => setCheckedIds(checked ? rows.map((row) => row.id) : []),
    [rows]
  );

  const clearChecked = useCallback(() => setCheckedIds([]), []);

  const handleTabChange = useCallback((index: number) => {
    setTabOverride(index);
    // Checked rows belong to the tab they were checked in — a bulk action spanning both
    // tabs cannot decide whether to activate or deactivate.
    setCheckedIds([]);
  }, []);

  const handleCopyCommand = useCallback(
    (chatbot: Chatbot) => {
      navigator.clipboard.writeText(`/start ${chatbot.short_code}`);
      toast({ title: 'Command copied to clipboard!', status: 'success', duration: 3000 });
    },
    [toast]
  );

  // Exports the Active tab as filtered, whichever tab is open.
  const handleDownloadQrCodes = useCallback(() => {
    qrExportMutation.mutate(
      activeWorkflows.map((workflow) => workflow.id),
      {
        onError: () =>
          toast({ title: 'Failed to download QR codes', status: 'error', duration: 5000 }),
      }
    );
  }, [qrExportMutation, activeWorkflows, toast]);

  const handleReorder = useCallback(
    (activeId: string, overId: string) => {
      const visible = tabIndex === 0 ? activeWorkflows : inactiveWorkflows;
      const from = visible.findIndex((workflow) => workflow.id === activeId);
      const to = visible.findIndex((workflow) => workflow.id === overId);
      if (from === -1 || to === -1) return;

      const moved = [...visible];
      const [row] = moved.splice(from, 1);
      moved.splice(to, 0, row);

      // Send both tabs so sort_order stays contiguous across the whole org. Sending only
      // the visible tab would renumber it 1..N and leave the other tab's numbers colliding.
      const other = tabIndex === 0 ? inactiveWorkflows : activeWorkflows;
      const fullOrder = tabIndex === 0 ? [...moved, ...other] : [...other, ...moved];
      const ids = fullOrder.map((workflow) => workflow.id);

      setPendingOrder(ids);
      reorderMutation.mutate(ids, {
        onSuccess: () => setPendingOrder(null),
        onError: (mutationError) => {
          setPendingOrder(null);
          toast({
            title:
              mutationError instanceof Error
                ? mutationError.message
                : 'Failed to save the new order',
            status: 'error',
            duration: 5000,
          });
        },
      });
    },
    [tabIndex, activeWorkflows, inactiveWorkflows, reorderMutation, toast]
  );

  const runBulk = useCallback(
    async (action: (id: string) => Promise<unknown>, successMessage: (count: number) => string) => {
      const ids = [...checkedIds];
      setBulkBusy(true);
      const failures: string[] = [];
      for (const id of ids) {
        try {
          await action(id);
        } catch {
          failures.push(id);
        }
      }
      setBulkBusy(false);
      setCheckedIds([]);
      const succeeded = ids.length - failures.length;
      if (succeeded > 0) {
        toast({ title: successMessage(succeeded), status: 'success', duration: 3000 });
      }
      if (failures.length > 0) {
        toast({
          title: `${failures.length} workflow${failures.length === 1 ? '' : 's'} could not be updated`,
          status: 'error',
          duration: 5000,
        });
      }
    },
    [checkedIds, toast]
  );

  // Same hazard as the single toggle, at greater scale — confirmed, not immediate.
  const handleBulkToggleStatus = useCallback(async () => {
    const blocking = tabIndex === 0;
    await runBulk(
      (id) => statusMutation.mutateAsync({ id, data: { status: blocking } }),
      (count) =>
        `${count} workflow${count === 1 ? '' : 's'} ${blocking ? 'deactivated' : 'activated'}`
    );
    bulkStatusDialog.onClose();
  }, [runBulk, statusMutation, tabIndex, bulkStatusDialog]);

  const handleBulkDelete = useCallback(async () => {
    await runBulk(
      (id) => deleteMutation.mutateAsync(id),
      (count) => `${count} workflow${count === 1 ? '' : 's'} deleted`
    );
    bulkDeleteDialog.onClose();
  }, [runBulk, deleteMutation, bulkDeleteDialog]);

  const breadcrumbItems = [
    { label: 'Dashboard', href: `/dashboard/${currentOrg!.id}/reports` },
    { label: 'Workflows' },
  ];

  if (isLoading) return <LoadingSpinner message="Loading workflows..." />;

  if (error) {
    return (
      <Center minH="100vh" bg="bg-canvas">
        <Text color="red.500">Error loading workflows: {error.message}</Text>
      </Center>
    );
  }

  const hasAnyWorkflows = Boolean(chatbots?.items?.length);
  const canReorder =
    sortMode === 'custom' &&
    !searchQuery.trim() &&
    typeFilter === 'all' &&
    departmentFilter === 'all';

  return (
    <PageContainer
      title="Workflows"
      description="Every workflow at a glance. Open the panel on any row to configure it."
      breadcrumbs={breadcrumbItems}
      maxW="1600px"
      headerAlign="left"
      actionPlacement="inline"
      action={
        hasGuide || canAdd ? (
          <HStack spacing={3}>
            {hasGuide && (
              <Button
                leftIcon={<Icon as={MdMenuBook} />}
                variant="outline"
                onClick={() => navigate(`/dashboard/${currentOrg!.id}/workflows/guide`)}
                size="lg"
                data-testid="wfl-guide-btn"
              >
                Guide
              </Button>
            )}
            {canAdd && (
              <Button
                leftIcon={<Icon as={MdAdd} />}
                colorScheme="brand"
                onClick={createDialog.onOpen}
                size="lg"
                data-testid="wfl-add-workflow-btn"
              >
                Add Workflow
              </Button>
            )}
          </HStack>
        ) : undefined
      }
    >
      {!hasAnyWorkflows ? (
        <EmptyState
          icon={<Icon as={MdAdd} boxSize={12} />}
          title="No workflows configured"
          description="There are no workflows set up for this organization yet."
        />
      ) : (
        <VStack align="stretch" spacing={4}>
          <HStack spacing={3} align="center" flexWrap="wrap" rowGap={2}>
            <InputGroup maxW="360px">
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="text-secondary" />
              </InputLeftElement>
              <Input
                data-testid="wfl-search-input"
                placeholder="Search by name or code…"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                bg="bg-surface"
              />
            </InputGroup>

            <Select
              maxW="170px"
              bg="bg-surface"
              data-testid="wfl-type-filter"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value as TypeFilter)}
              aria-label="Filter by bot type"
            >
              <option value="all">All types</option>
              <option value="static">Checklist</option>
              <option value="form">Form</option>
            </Select>

            <Select
              maxW="200px"
              bg="bg-surface"
              data-testid="wfl-department-filter"
              value={departmentFilter}
              onChange={(event) => setDepartmentFilter(event.target.value)}
              aria-label="Filter by department"
            >
              <option value="all">All departments</option>
              {departments.map((department) => (
                <option key={department.key} value={department.key}>
                  {department.label}
                </option>
              ))}
              <option value="none">No department</option>
            </Select>

            <Tooltip
              label={
                sortMode === 'custom' && !canReorder
                  ? 'Clear the search and filters to drag workflows into a new order'
                  : undefined
              }
              placement="top"
            >
              <Select
                maxW="200px"
                bg="bg-surface"
                data-testid="wfl-sort-select"
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value as SortMode)}
                aria-label="Sort workflows"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    Sort: {option.label}
                  </option>
                ))}
              </Select>
            </Tooltip>

            <Box flex={1} />

            {searchQuery && (
              <Text fontSize="sm" color="text-secondary">
                Found {activeWorkflows.length + inactiveWorkflows.length} workflow
                {activeWorkflows.length + inactiveWorkflows.length !== 1 ? 's' : ''}
              </Text>
            )}

            <Tooltip label="Excel of the Active tab's workflows, as filtered" placement="top">
              <Button
                leftIcon={<Icon as={MdQrCode2} />}
                variant="outline"
                onClick={handleDownloadQrCodes}
                isLoading={qrExportMutation.isPending}
                isDisabled={activeWorkflows.length === 0}
                data-testid="wfl-download-qr-btn"
              >
                Download QR codes
              </Button>
            </Tooltip>
          </HStack>

          <Tabs index={tabIndex} onChange={handleTabChange} colorScheme="brand" variant="line">
            <TabList>
              <Tab data-testid="wfl-tab-active">
                Active
                <Badge ml={2} colorScheme="green" variant="subtle">{activeWorkflows.length}</Badge>
              </Tab>
              <Tab data-testid="wfl-tab-inactive">
                Inactive
                <Badge ml={2} colorScheme="gray" variant="subtle">{inactiveWorkflows.length}</Badge>
              </Tab>
            </TabList>
          </Tabs>

          {checkedIds.length > 0 && (
            <WorkflowBulkBar
              count={checkedIds.length}
              showingActive={tabIndex === 0}
              isBusy={isBulkBusy}
              onToggleStatus={bulkStatusDialog.onOpen}
              onDelete={bulkDeleteDialog.onOpen}
              onClear={clearChecked}
            />
          )}

          <WorkflowTable
            rows={rows}
            checkedIds={checkedIds}
            openId={openWorkflow?.id}
            canReorder={canReorder}
            reminderSummary={reminderSummary}
            onCheck={handleCheck}
            onCheckAll={handleCheckAll}
            onConfigure={handleConfigure}
            onCopyCommand={handleCopyCommand}
            onShowQr={setQrWorkflow}
            onReorder={handleReorder}
          />

          <Text fontSize="sm" color="text-secondary">
            Showing {rows.length} workflow{rows.length !== 1 ? 's' : ''}
          </Text>
        </VStack>
      )}

      <WorkflowConfigPanel
        chatbot={openWorkflow}
        onClose={clearSelection}
        onDeleted={clearChecked}
      />

      <WorkflowQrModal chatbot={qrWorkflow} onClose={() => setQrWorkflow(null)} />

      <ConfirmDialog
        isOpen={bulkStatusDialog.isOpen}
        onClose={bulkStatusDialog.onClose}
        onConfirm={handleBulkToggleStatus}
        title={tabIndex === 0 ? 'Deactivate Workflows' : 'Activate Workflows'}
        message={
          tabIndex === 0
            ? `Deactivate ${checkedIds.length} workflow${checkedIds.length === 1 ? '' : 's'}? They stop accepting WhatsApp requests immediately.`
            : `Activate ${checkedIds.length} workflow${checkedIds.length === 1 ? '' : 's'}? They start accepting WhatsApp requests again immediately.`
        }
        confirmText={tabIndex === 0 ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        isLoading={isBulkBusy}
        colorScheme={tabIndex === 0 ? 'red' : 'brand'}
      />

      <ConfirmDialog
        isOpen={bulkDeleteDialog.isOpen}
        onClose={bulkDeleteDialog.onClose}
        onConfirm={handleBulkDelete}
        title="Delete Workflows"
        message={`Are you sure you want to delete ${checkedIds.length} workflow${
          checkedIds.length === 1 ? '' : 's'
        }? This action cannot be undone.`}
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={isBulkBusy}
        colorScheme="red"
      />

      <CreateMethodDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.onClose}
        orgId={currentOrg?.id || ''}
      />
    </PageContainer>
  );
}
